import {
	AimOutlined,
	AppstoreOutlined,
	CalendarOutlined,
	CameraOutlined,
	ColumnWidthOutlined,
	CopyOutlined,
	FileTextOutlined,
	HomeOutlined,
	LogoutOutlined,
	PictureOutlined,
	PlayCircleOutlined,
	SelectOutlined,
	StarOutlined,
	TagsOutlined,
	UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Button, Menu, Select } from 'antd';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logoutUser } from 'src/lib/firebase/fbClient'; 
import { QueryGetRegions } from 'src/lib/gql/queries';
import { ROUTES } from '../config/route';
import logo from '../logo.svg';
import { CmsGetRegionsQuery, Role } from 'src/lib/generated/_generated';

const { Option } = Select;

interface Props {
	isPhotographer?: boolean;
	collapse?: boolean;
}

const Navbar: React.FunctionComponent<Props> = ({collapse}: Props) => {
	const location = useLocation();
	//const [, setUserToken] = useRecoilState(userTokenAtom);

	const user = JSON.parse(localStorage.getItem('authUser') as string);
	useNavigate();
	const fnLogout = async () => {
		await logoutUser();
		window.location.href = '/';
	};

	const { data: qryData } = useQuery<CmsGetRegionsQuery>(QueryGetRegions);

	const photographerMenuItems = [
		{
			key: 'photographer',
			icon: <HomeOutlined />,
			label: <Link to={ROUTES.PHOTOGRAPHER_HOME.path}>Events Calendar</Link>,
		},
		{
			key: 'events/assign',
			icon: <CameraOutlined />,
			label: (
				<Link to={ROUTES.PHOTOGRAPHER_EVENTSELFASSIGN.path}>
					Assign to Events
				</Link>
			),
		},
	];

	const adminMenuItems = [
		{
			key: 'intro-video',
			icon: <HomeOutlined />,
			label: <Link to={ROUTES.INTRO_VIDEO.path}>Intro Video</Link>,
		},
		{
			key: 'stories',
			icon: <PictureOutlined />,
			label: <Link to={ROUTES.STORIES.path}>Story</Link>,
		},
		{
			key: 'slider',
			icon: <ColumnWidthOutlined />,
			label: <Link to={ROUTES.SLIDERS.path}>Slider</Link>,
		},
		{
			key: 'news',
			icon: <CopyOutlined />,
			label: 'News / Articles',
			children: [
				{
					key: 'news-list',
					label: <Link to={ROUTES.NEWS.path}>List of Articles</Link>,
				},
				{
					key: 'news-comments',
					label: <Link to={ROUTES.NEWS_COMMENTS.path}>Comments</Link>,
				},
				{
					key: 'news-categories',
					label: <Link to={ROUTES.NEWS_CATEGORIES.path}>News Categories</Link>,
				},
				{
					key: 'news-featuredlist',
					label: <Link to={ROUTES.EDIT_FEATURED_LIST.path}>Featured List</Link>,
				},
			],
		},
		{
			key: 'things-to-do',
			icon: <AppstoreOutlined />,
			label: <Link to={ROUTES.THINGS_TO_DO.path}>Things to Do</Link>,
		},
		{
			key: 'events',
			icon: <CalendarOutlined />,
			label: 'Events',
			children: [
				{
					key: 'events-calendar',
					label: <Link to={ROUTES.EVENTS.path}>Event Calendar</Link>,
				},
				{
					key: 'events-pending',
					label: (
						<Link to={ROUTES.VIEW_PENDING_EVENTS.path}>Event Activation</Link>
					),
				},
				{
					key: 'events-photographers',
					label: (
						<Link to={ROUTES.EVENT_PHOTOGRAPHER.path}>Event Photographer</Link>
					),
				},
				{
					key: 'events-genres',
					label: <Link to={ROUTES.EVENTS_GENRES.path}>Events Genres</Link>,
				},
			],
		},
		{
			key: 'best-places',
			icon: <StarOutlined />,
			label: <Link to={ROUTES.BEST_PLACES.path}>Best Places in Town</Link>,
		},
		{
			key: 'discover-the-city',
			icon: <SelectOutlined />,
			label: <Link to={ROUTES.DISCOVER_CITY.path}>Discover the City</Link>,
		},
		{
			key: 'venues',
			icon: <AimOutlined />,
			label: <Link to={ROUTES.VENUES.path}>Venues</Link>,
		},
		{
			key: 'photos',
			icon: <PictureOutlined />,
			label: <Link to={ROUTES.PHOTOS.path}>Photos</Link>,
		},
		{
			key: 'videos',
			icon: <PlayCircleOutlined />,
			label: 'Videos',
			children: [
				{
					key: 'videos-all',
					label: <Link to={ROUTES.VIDEOS.path}>All Videos</Link>,
				},
				{
					key: 'videos-categories',
					label: (
						<Link to={ROUTES.VIDEOS_CATEGORY.path}>Videos Categories</Link>
					),
				},
				{
					key: 'youtube-videos',
					label: <Link to={ROUTES.YOUTUBE_VIDEOS.path}>YouTube Playlist</Link>,
				},
			],
		},
		{
			key: 'offers',
			icon: <TagsOutlined />,
			label: 'Offers',
			children: [
				{
					key: 'offers-all',
					label: <Link to={ROUTES.OFFERS.path}>Offers</Link>,
				},
				{
					key: 'offers-categories',
					label: (
						<Link to={ROUTES.OFFERS_CATEGORIES.path}>Offers Categories</Link>
					),
				},
				{
					key: 'offers-activation',
					label: (
						<Link to={ROUTES.VIEW_PENDING_OFFERS.path}>Offer requests</Link>
					),
				},
			],
		},
		{
			key: 'popup-ad',
			icon: <HomeOutlined />,
			label: <Link to={ROUTES.POPUP_AD.path}>Popup Ad</Link>,
		},
		{
			key: 'static-pages',
			icon: <FileTextOutlined />,
			label: 'Static Pages',
			children: [
				{
					key: 'static-pages-list',
					label: <Link to={ROUTES.STATIC_PAGES.path}>All Pages</Link>,
				},
				{
					key: 'static-pages-about-us',
					label: <Link to={ROUTES.EDIT_ABOUT_US.path}>About Us</Link>,
				},
			],
		},
		{
			key: 'users',
			icon: <UserOutlined />,
			label: 'Users',
			children: [
				{
					key: 'users-all',
					label:
						user?.type === Role.SuperAdmin ? (
							<Link to={ROUTES.USERS.path}>All Users</Link>
						) : null,
				},
				{
					key: 'users-visitors',
					label: <Link to={ROUTES.VISITORS.path}>Visitors</Link>,
				},
				{
					key: 'users-authors',
					label: <Link to={ROUTES.AUTHORS.path}>Authors</Link>,
				},
				{
					key: 'users-photographers',
					label: <Link to={ROUTES.PHOTOGRAPHERS.path}>Photographers</Link>,
				},
				{
					key: 'users-photographers-requests',
					label: (
						<Link to={ROUTES.PHOTOGRAPHERS_REQUESTS.path}>
							Photographers Requests
						</Link>
					),
				},
			].filter((item) => item.label !== null),
		},
	];

	return (
		<div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					flexDirection: 'column',
					alignItems: 'center',
					margin: '24px 0 16px 0',
					padding: '0 10px',
				}}
			>
				<img
					src={`${logo}`}
					alt="Dubai Night"
					style={{
						maxWidth: '150px',
						width: '100%',
					}}
				/>
				<div
					style={{
						width: '100%',
						marginTop: '1.15rem',
					}}
				>
					<Select
						placeholder="Select Region"
						style={{
							width: '100%',
						}}
						dropdownStyle={{
							left: collapse ? '85px' : '10px',
							position: 'relative',
							width: '220px',
						}}
						onChange={(e) => {
							window.open(`https://${e}`, '_blank');
						}}
					>
						{qryData?.getRegions?.map((regions, i) => (
							<Option key={'Region' + i} value={regions.cmsDomainName || ''}>
								{regions.name}
							</Option>
						))}
					</Select>
				</div>

				<Button
					onClick={fnLogout}
					key="logoutAd"
					icon={<LogoutOutlined style={{ color: 'white' }} />}
					style={{
						color: 'white',
						backgroundColor: 'var(--invalid)',
						borderRadius: '5px',
						marginTop: '1rem',
						width: '100%',
						maxWidth: '50%',
					}}
				>
					{collapse? '' : 'Logout'}
				</Button>
			</div>

			<Menu
				defaultSelectedKeys={[location.pathname.replace('/', '')]}
				selectedKeys={[location.pathname.replace('/', '')]}
				mode="inline"
				theme="light"
				items={
					user?.type === Role.Photographer
						? photographerMenuItems
						: adminMenuItems
				}
			/>
		</div>
	);
};

export default Navbar;
